import createTheme from '@mui/material/styles/createTheme'
import ThemeProvider from '@mui/material/styles/ThemeProvider'
import useMediaQuery from '@mui/material/useMediaQuery'
import CssBaseline from '@mui/material/CssBaseline'
import type {PaletteMode, Theme, ThemeOptions} from "@mui/material";
import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import rtlPlugin from 'stylis-plugin-rtl';
import {CacheProvider} from '@emotion/react';
import createCache from '@emotion/cache'
import useMeta from "../hooks/Api/useMeta";
import {lighten} from "@mui/material";

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [rtlPlugin],
});

const RTL: React.FC = (props) => {
    return <CacheProvider value={cacheRtl}>{props.children}</CacheProvider>;
}


export const defaultThemeOptions: ThemeOptions = {
    palette: {
        primary: {
            main: "#eb7c1f"
        },
        // secondary: {
        //     main: "#634795"
        // },
        // success: {
        //     main: '#33DB33'
        // },
        // error: {
        //     main: '#EC2233'
        // },
        // warning: {
        //     main: '#F5C24D'
        // },

        // mode: 'dark'
    },
    typography: {
        fontFamily: '"Tajawal", "Montserrat", sans-serif'
    },
    direction: 'rtl',
    components: {
        MuiCard: {
            styleOverrides: {
                root: theme => ({
                    background: lighten(theme.theme.palette.background.paper, 0.05)
                })
            }
        }
    },
};


type ThemeHook = {
    themeOptions: ThemeOptions;
    setThemeOptions: React.Dispatch<React.SetStateAction<ThemeOptions>>;
    theme: Theme;
    readonly themeMode: PaletteMode;
    toggleThemeMode: (mode?: PaletteMode) => void;
    isDarkMode: boolean,
    isLightMode: boolean,
}

function useThemeSetup(): ThemeHook {
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)")
    const [mode, _setMode] = useState<PaletteMode>(defaultThemeOptions?.palette?.mode || localStorage.themeMode);
    const setMode = useCallback<React.Dispatch<React.SetStateAction<PaletteMode>>>((m) => {
        let newMode = m instanceof Function ? m(mode) : m;
        _setMode(newMode);
        localStorage.themeMode = newMode;
    }, [mode])
    const [themeOptions, setThemeOptions] = useState(defaultThemeOptions);
    const [theme, setTheme] = useState(createTheme(themeOptions));
    const toggleMode = useCallback((m: PaletteMode | any = mode === 'light' ? 'dark' : mode === 'dark' ? undefined : 'light') => setMode(m), [mode])

    const isDarkMode = useMemo(() => mode ? mode === 'dark' : prefersDarkMode, [prefersDarkMode, mode]);
    const isLightMode = useMemo(() => mode === 'light', [mode]);

    useEffect(() => {
        themeOptions.palette = themeOptions?.palette ?? {};
        themeOptions.palette.mode = isDarkMode ? 'dark' : 'light';
        themeOptions.palette.background = themeOptions?.palette.background ?? {};
        themeOptions.palette.background.default = isDarkMode ? '#13191c' : '#F2F2F3'
        themeOptions.palette.background.paper = isDarkMode ? '#0f1417' : '#FFF'
        setTheme(createTheme(themeOptions));
    }, [mode, themeOptions, prefersDarkMode])

    useEffect(() => {
        document.documentElement.dir = theme.direction
        // <meta name="color-scheme" content="light dark">
        let colorSchemeMeta = document.head.querySelector<HTMLMetaElement>(`meta[name="color-scheme"]`)
        if (!colorSchemeMeta) {
            colorSchemeMeta = document.createElement('meta')
            colorSchemeMeta.name = "color-scheme";
            document.head.append(colorSchemeMeta)
        }
        colorSchemeMeta.content = theme.palette.mode
    }, [theme])
    return {
        toggleThemeMode: toggleMode,
        isDarkMode,
        isLightMode,
        themeMode: mode,
        themeOptions,
        setThemeOptions,
        theme,
    }
}


const ThemeContext = createContext<ThemeHook>({
    toggleThemeMode: mode => {
    },
    themeMode: defaultThemeOptions?.palette?.mode || 'light',
    isDarkMode: defaultThemeOptions?.palette?.mode === 'dark',
    isLightMode: defaultThemeOptions?.palette?.mode === 'light',
    themeOptions: defaultThemeOptions,
    setThemeOptions: theme => {
    },
    theme: createTheme(defaultThemeOptions),
});

const ThemeProvide: React.FC = (props) => {
    const theme = useThemeSetup();
    const {data: meta} = useMeta()


    // useEffect(() => {
    //     if (meta) {
    //         theme.setThemeOptions({
    //             ...theme.themeOptions,
    //             palette: {
    //                 ...theme.themeOptions.palette,
    //                 primary: {
    //                     main: meta?.customization?.app_color_primary as string || '#f',
    //                 },
    //                 secondary: {
    //                     main: meta?.customization?.app_color_secondary as string || '',
    //                 }
    //             }
    //         })
    //     }
    // }, [meta?.customization?.app_color_primary, meta?.customization?.app_color_secondary])
    //

    return (
        <ThemeContext.Provider value={theme}>
            <ThemeProvider theme={theme.theme}>
                <RTL>
                    <CssBaseline/>
                    <style
                        // @ts-ignore
                        jsx={""}>
                        {`
                          body {
                            background: ${theme.theme.palette.background.default};
                            color: ${theme.theme.palette.text.primary};
                            font-family: ${theme.theme.typography.fontFamily} !important;
                          }

                          .logo-image {
                            filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.5));
                            object-position: center center;
                            object-fit: contain;
                          }

                          .logo-image-text {
                            filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.5));
                          }

                          #nprogress .bar, #nprogress .spinner-icon {
                            z-index: 10000000000000000000000;
                          }

                          #nprogress .bar {
                            background: ${theme.theme.palette.primary.main};
                            height: 5px
                          }

                          #nprogress .peg {
                            box-shadow: 0 0 10px ${theme.theme.palette.primary.main}, 0 0 5px ${theme.theme.palette.primary.main};
                          }

                          #nprogress .spinner-icon {
                            display: none;
                              // border-top-color: ${theme.theme.palette.primary.main} !important;
                              // border-left-color: ${theme.theme.palette.primary.main} !important;
                          }
                        `}
                    </style>
                    {meta && props.children}
                </RTL>
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};

const useTheme = () => useContext(ThemeContext);

export {useTheme, ThemeProvide as ThemeProvider};
